import React, { useState } from "react";
import { Trash2, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { syShort } from "@/components/LetterProgressBar";

// Coach discretion: award / remove Subsequent Letters by hand (reason note required). Saves immediately.
export function MemberLetterAwards({ memberId, status, onChanged }) {
  const [ls, setLs] = useState(status || {});
  const [year, setYear] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const { data } = await api.get(`/coach/team-members/${memberId}`);
    setLs(data.letter_status);
    onChanged?.();
  };
  const add = async () => {
    if (!year || !reason.trim()) { toast.error("Pick a school year and add a reason note"); return; }
    setBusy(true);
    try { await api.post(`/coach/team-members/${memberId}/letter-awards`, { year: Number(year), reason: reason.trim() }); toast.success("Subsequent Letter awarded"); setYear(""); setReason(""); await refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/coach/team-members/${memberId}/letter-awards/${id}`); toast.success("Award removed"); await refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const taken = new Set(ls.additional_award_years || []);
  const years = ls.first_letter_earned
    ? Array.from({ length: Math.max(0, ls.active_year - ls.first_letter_year) }, (_, i) => ls.active_year - i).filter((y) => !taken.has(y))
    : [];

  return (
    <div className="space-y-2 rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="coach-member-letter-awards">
      <Label className="flex items-center gap-1.5"><Award className="h-4 w-4 text-violet-600" /> Subsequent Letters by coach</Label>
      {(ls.manual_awards || []).map((a) => (
        <div key={a.id} className="flex items-start gap-2 rounded-md bg-white px-2.5 py-1.5 text-sm" data-testid={`coach-letter-award-${a.id}`}>
          <span className="font-semibold tabular-nums text-violet-800">{syShort(a.year)}</span>
          <span className="min-w-0 flex-1 text-slate-600">{a.reason}{a.awarded_by_name ? <span className="text-xs text-slate-400"> · {a.awarded_by_name}</span> : null}
            {!a.counted && <span className="ml-1 rounded-full bg-slate-100 px-1.5 text-[10px] font-semibold text-slate-500">not counted</span>}</span>
          <button type="button" onClick={() => remove(a.id)} className="text-red-500 hover:text-red-700" data-testid={`coach-letter-award-remove-${a.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      {!ls.first_letter_earned ? <p className="text-xs text-slate-500">Award the First Letter first — Subsequent Letters start the school year after it.</p>
        : years.length === 0 ? <p className="text-xs text-slate-500">Every eligible school year already has an award (one per year).</p>
        : (
          <div className="flex flex-wrap items-center gap-2">
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="h-8 w-32" data-testid="coach-letter-award-year"><SelectValue placeholder="School year" /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)} data-testid={`coach-letter-award-year-${y}`}>{syShort(y)}</SelectItem>)}</SelectContent>
            </Select>
            <Input className="h-8 min-w-[10rem] flex-1" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason note (required)" data-testid="coach-letter-award-reason" />
            <Button type="button" size="sm" disabled={busy} onClick={add} className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="coach-letter-award-add">Award</Button>
          </div>
        )}
    </div>
  );
}

// School years the member sat out — each one breaks the Active-years streak.
export function MissedYearsPicker({ joinYear, curYear, value, onChange }) {
  if (!joinYear || !curYear) return null;
  const years = Array.from({ length: curYear - joinYear + 1 }, (_, i) => joinYear + i);
  return (
    <div className="space-y-2" data-testid="coach-member-missed-years">
      <Label>Missed school years</Label>
      <div className="flex flex-wrap gap-2">
        {years.map((y) => {
          const on = value.includes(y);
          return (
            <button type="button" key={y} onClick={() => onChange(on ? value.filter((x) => x !== y) : [...value, y])} data-testid={`coach-member-missed-year-${y}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${on ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {syShort(y)}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-slate-400">Tap a year the member didn't complete. It breaks their Active-years streak for lettering.</p>
    </div>
  );
}
